import React from "react"
import { useTranslation } from "react-i18next"
import { Dropdown, Menu, message, Table, Tooltip } from "antd"
import { IWebhook } from "@/types/webhooks.types"
import ConfirmModal from "@/UI/Modals/ConfirmModal"
import { faEllipsis, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import WebhooksAddEditModal from "./WebhooksAddEditModal"
import { getMaskedString } from "@/utils/strings"

const columns = (
  t: any,
  webhooks: IWebhook[],
  handleUrlClick: (url: string) => void,
  updateWebhook: (values: IWebhook) => Promise<Error | null>,
  deleteWebhook: (id: string) => Promise<Error | null>
) => [
  {
    title: "URL",
    dataIndex: "url",
    key: "1",
    width: "50%",
    render: (url: string) => {
      return (
        <p
          className="my-auto whitespace-nowrap text-ellipsis overflow-hidden cursor-pointer font-medium"
          onClick={() => handleUrlClick(url)}
        >
          {url}
        </p>
      )
    },
  },
  {
    title: "SIGNING SECRET",
    key: "2",
    width: "40%",
    render: (webhook: IWebhook) => {
      return <WebhookSecret webhook={webhook} />
    },
  },
  {
    key: "3",
    width: "10%",
    render: (webhook: IWebhook) => {
      const onDelete = async () => {
        try {
          if (!webhook.id) {
            message.error("developers.Webhooks.noWebhookIdError")
            return
          }
          await deleteWebhook(webhook.id)
          message.success(t("developers.Webhooks.deleteWebhookSuccess"))
        } catch (error) {
          message.error(t("developers.Webhooks.deleteWebhookError"))
        }
      }

      return (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu id="webhooks-table-dropdown">
              <Menu.Item>
                <WebhooksAddEditModal
                  title={t("developers.Webhooks.editWebhook")}
                  button={<div className="h-full">{t("common.edit")}</div>}
                  handleSubmit={updateWebhook}
                  webhook={
                    {
                      id: webhook.id,
                      url: webhook.url,
                    } as IWebhook
                  }
                  webhooks={webhooks}
                />
              </Menu.Item>
              <Menu.Item>
                <ConfirmModal
                  button={<div className="h-full">{t("common.delete")}</div>}
                  content={{
                    type: "danger",
                    title: t("developers.Webhooks.deleteWebhook"),
                    description: t("developers.Webhooks.deleteWebhookConfirmation"),
                    cancelText: t("common.cancel"),
                    okText: t("common.yesDelete"),
                  }}
                  onConfirm={onDelete}
                />
              </Menu.Item>
            </Menu>
          }
        >
          <div className="flex items-center justify-center">
            <FontAwesomeIcon
              className="flex-shrink-0 h-4 text-gray-500 text-sm cursor-pointer"
              icon={faEllipsis}
            />
          </div>
        </Dropdown>
      )
    },
  },
]

const WebhookSecret: React.FC<{ webhook: IWebhook }> = ({ webhook }) => {
  const { t } = useTranslation()
  const [isHidden, setIsHidden] = React.useState(true)

  return (
    <div className="flex-shrink-0 my-auto whitespace-wrap">
      <div className="flex items-center">
        <Tooltip
          title={isHidden ? t("developers.Webhooks.revealKey") : t("developers.Webhooks.hideKey")}
        >
          <FontAwesomeIcon
            icon={isHidden ? faEye : faEyeSlash}
            className="mr-2 text-gray-600 cursor-pointer w-[18px] select-none"
            onClick={() => setIsHidden(!isHidden)}
          />
        </Tooltip>
        <p className={`${isHidden && "text-gray-400"}`}>
          {isHidden ? getMaskedString(30) : webhook.key}
        </p>
      </div>
    </div>
  )
}

interface WebhooksTableProps {
  webhooks: IWebhook[]
  setViewDetailWebhook: (webhook: IWebhook | null) => void
  updateWebhook: (values: IWebhook) => Promise<Error | null>
  removeWebhook: (id: string) => Promise<Error | null>
}

const WebhooksTable: React.FC<WebhooksTableProps> = ({
  webhooks,
  setViewDetailWebhook,
  updateWebhook,
  removeWebhook,
}) => {
  const { t } = useTranslation()

  const handleUrlClick = (key: string) => {
    setViewDetailWebhook(webhooks.find((item) => item.url === key) || null)
  }

  return (
    <Table
      id="webhooks-table"
      className="order-table !mt-0"
      size="small"
      rowKey={"id"}
      dataSource={webhooks}
      columns={columns(t, webhooks, handleUrlClick, updateWebhook, removeWebhook)}
      pagination={{
        showSizeChanger: false,
        total: webhooks.length,
      }}
      scroll={{ x: 800 }}
    />
  )
}

export default WebhooksTable
